import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDataProvider } from 'react-admin';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Chip,
  Typography,
  Box,
  makeStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

import { computeDocumentCompleteness } from '../../../helpers';
import UploadDocumentDialog from '../../Cleaner/detail/ShowActions/UploadDocumentDialog';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      marginTop: 16,
    },
    present: {
      color: theme.palette.success.main,
    },
    missing: {
      color: theme.palette.text.disabled,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(0.5, 0),
    },
    hint: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(1),
    },
  };
});

// Read-only document-completeness checklist (plan 013 B3), derived from
// cleaner_document rows of an APPROVED status, aggregated by silae_id. The
// slot matching (incl. the subtype-sensitive identité/vitale split, both under
// type='identity') lives in helpers/vivier.js#computeDocumentCompleteness.
const DocumentCompletenessCard = ({ silaeId, sourceCleanerId }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const [slots, setSlots] = useState(computeDocumentCompleteness([]));
  const [loaded, setLoaded] = useState(false);
  // Bumped after an upload to force the completeness re-fetch. The silae_id is
  // back-filled by the care trigger, so an immediate refetch may not yet show
  // the new doc — but the user can re-open the page and it'll be reflected.
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!silaeId) {
      setSlots(computeDocumentCompleteness([]));
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    dataProvider
      .getList('cleaner_document', {
        sort: { field: 'upload_date', order: 'DESC' },
        pagination: { page: 1, perPage: 200 },
        filter: { silae_id: silaeId, status: 'APPROVED' },
      })
      .then(({ data }) => {
        if (!cancelled) {
          setSlots(computeDocumentCompleteness(data || []));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSlots(computeDocumentCompleteness([]));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [silaeId, dataProvider, refreshToken]);

  const completeCount = slots.filter((s) => {
    return s.present;
  }).length;

  // Upload re-uses the cleaner document pipeline: the dialog dispatches the
  // uploadCleanerDocument saga (POST /cleanerDocument/upload) keyed on the
  // cleaner record id — here `person_vivier.id` (= the cleaner_id).
  const onUploaded = () => {
    setRefreshToken((t) => {
      return t + 1;
    });
  };

  return (
    <Grid item xs={12} className={classes.root}>
      <Card>
        <CardHeader
          title="Complétude documents"
          subheader={
            silaeId
              ? `${completeCount}/${slots.length} documents approuvés`
              : 'Aucun matricule — complétude indisponible'
          }
          action={
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Chip
                size="small"
                color={completeCount === slots.length ? 'primary' : 'default'}
                label={`${completeCount}/${slots.length}`}
              />
              {sourceCleanerId ? (
                <UploadDocumentDialog
                  record={{ id: sourceCleanerId }}
                  additionalActionOnSubmit={onUploaded}
                />
              ) : null}
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={1}>
            {slots.map((slot) => {
              return (
                <Grid item xs={12} sm={6} key={slot.key}>
                  <div className={classes.row}>
                    {slot.present ? (
                      <CheckCircleIcon
                        fontSize="small"
                        className={classes.present}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        fontSize="small"
                        className={classes.missing}
                      />
                    )}
                    <Typography
                      variant="body2"
                      className={slot.present ? undefined : classes.missing}
                    >
                      {slot.label}
                    </Typography>
                  </div>
                </Grid>
              );
            })}
          </Grid>
          {loaded && !silaeId && (
            <Typography variant="caption" className={classes.hint}>
              La complétude documentaire est agrégée par matricule (silae_id).
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

DocumentCompletenessCard.propTypes = {
  silaeId: PropTypes.string,
  sourceCleanerId: PropTypes.string,
};

DocumentCompletenessCard.defaultProps = {
  silaeId: '',
  sourceCleanerId: '',
};

export default DocumentCompletenessCard;
